import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
// Mantendo os imports originais conforme solicitado
import 'react-toastify/dist/ReactToastify.css';
import {
    FaWarehouse, FaSignOutAlt, FaPlus, FaExchangeAlt, FaSearch, FaBarcode, FaTimes, FaPrint, FaQrcode, FaEdit, FaSave // Adicionado FaEdit e FaSave
} from 'react-icons/fa';
// **MUDANÇA**: Importando a nova biblioteca de scanner
import { Html5QrcodeScanner } from 'html5-qrcode'; // Substituído @yudiel/react-qr-scanner
// Reintroduzindo a biblioteca de QR code
import QRCode from 'qrcode.react';
// Reintroduzindo hook para impressão
import { useReactToPrint } from 'react-to-print';


// --- ESTILOS CSS ADICIONAIS ---
const printStyles = `
  @media print {
    body * {
      visibility: hidden;
    }
    .printable-qr, .printable-qr * {
      visibility: visible;
    }
    .printable-qr {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      padding: 20px; /* Adiciona algum padding na impressão */
      border: 1px solid #ccc; /* Adiciona borda simples na impressão */
      background-color: white !important; /* Garante fundo branco */
      -webkit-print-color-adjust: exact; /* Força cores de fundo no Chrome/Safari */
      color-adjust: exact; /* Força cores de fundo em outros browsers */
    }
    .no-print {
        display: none !important; /* Garante que não imprima */
    }
     /* Esconde outros modais durante a impressão */
    .modal-overlay:not(:has(.printable-qr)) {
        display: none !important;
    }
    /* Esconde o ToastContainer durante a impressão */
    .Toastify {
        display: none !important;
    }
     /* Garante que o header da página não seja impresso */
     header { display: none !important; }
     /* Garante que o main container não afete a posição absoluta */
     main { position: static !important; }

  }
`;

// Hook customizado para adicionar estilos de impressão
const usePrintStyles = () => {
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = printStyles;
    document.head.appendChild(styleSheet);
    // Limpa ao desmontar
    return () => {
      if (document.head.contains(styleSheet)) {
          document.head.removeChild(styleSheet);
      }
    };
  }, []); // Executa apenas uma vez
};


// --- MODAL: Leitor de Código de Barras ---
// **MUDANÇA**: Atualizado para usar html5-qrcode
const BarcodeScannerModal = ({ onClose, onScan, isFetchingData }) => {
    const scannerRef = useRef(null); // Ref para o objeto do scanner
    const readerId = "qr-reader-element"; // ID do elemento onde o scanner vai renderizar

    useEffect(() => {
        // Só inicializa o scanner se ele não estiver já instanciado
        if (!scannerRef.current) {
            const html5QrcodeScanner = new Html5QrcodeScanner(
                readerId,
                {
                    fps: 10, // Quadros por segundo
                    qrbox: { width: 250, height: 250 }, // Tamanho da "mira"
                    rememberLastUsedCamera: true,
                    supportedScanTypes: [0] // 0 = Câmera (DEFAULT_SCAN_TYPE_CAMERA)
                },
                false // verbose = false
            );

            const onScanSuccess = (decodedText, decodedResult) => {
                // `decodedText` é o texto lido do QR code
                if (decodedText && !isFetchingData) {
                    onScan(decodedText); // Chama a função onScan (handleBarcodeScanned)
                    // O onScan agora fecha o modal principal (setShowScanner(false))
                }
            };

            const onScanFailure = (error) => {
                // Ignora erros comuns de "QR code not found"
                if (!error.includes("QR code not found") && !error.includes("service failure")) {
                     console.warn("Falha no scan (html5-qrcode):", error);
                }
            };

            // Começa a renderizar o scanner
            html5QrcodeScanner.render(onScanSuccess, onScanFailure);
            
            // Armazena o objeto do scanner na ref para poder limpá-lo depois
            scannerRef.current = html5QrcodeScanner;
        }

        // Função de limpeza (quando o modal fecha)
        return () => {
            if (scannerRef.current) {
                // Tenta parar o scanner e limpar
                 scannerRef.current.clear().catch(error => {
                     console.error("Falha ao limpar html5-qrcode scanner:", error);
                 });
                 scannerRef.current = null; // Reseta a ref
            }
        };
    // Adicionamos 'onScan' e 'isFetchingData' como dependências
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetchingData]); // Removido onScan para evitar recriação desnecessária

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-[60] p-4 modal-overlay">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 relative">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">Aponte para o Código QR</h2>
                    <button onClick={onClose} disabled={isFetchingData} className="text-gray-400 hover:text-gray-600 absolute top-3 right-3 no-print disabled:opacity-50"><FaTimes size={20} /></button>
                </div>
                {isFetchingData && ( <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg z-10"><p className="text-lg font-semibold text-gray-700">Processando...</p></div> )}
                {/* **MUDANÇA**: Este div é o container onde o html5-qrcode vai se renderizar */}
                <div id={readerId} className="w-full h-auto overflow-hidden rounded-lg border relative">
                    {/* A biblioteca html5-qrcode vai injetar o vídeo aqui */}
                </div>
                <p className="text-center text-sm text-gray-500 mt-3">A câmera será ativada.</p>
            </div>
        </div>
    );
};


// --- MODAL: Exibir e Imprimir QR Code ---
// (Sem alterações)
const QrCodeModal = ({ isOpen, onClose, product }) => {
    const qrCodeRef = useRef();
    const handlePrint = useReactToPrint({ content: () => qrCodeRef.current, documentTitle: `QRCode-${product?.nome?.replace(/\s+/g, '_') || 'Produto'}` });
    if (!isOpen || !product) return null;
    const qrData = product._id || product.codigoBarras; // Prioriza o ID do Mongo
    if (!qrData) { console.error("Sem ID/Código."); toast.error("Não é possível gerar QR (sem ID/Código)."); onClose(); return null; }
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[70] p-4 modal-overlay">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-xs p-6 relative">
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 absolute top-3 right-3 no-print"><FaTimes size={20} /></button>
                <div ref={qrCodeRef} className="printable-qr text-center">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">{product.nome}</h3>
                    <div className="flex justify-center my-4"> <QRCode value={qrData} size={180} level={"H"} includeMargin={true} /> </div>
                    <p className="text-xs text-gray-500 break-all">{qrData}</p>
                </div>
                <div className="mt-6 text-center no-print"> <button onClick={handlePrint} className="btn btn-primary w-full"> <FaPrint className="mr-2" /> Imprimir </button> </div>
            </div>
        </div>
    );
};

// --- MODAL: Editar Produto ---
// (Sem alterações)
const EditProductModal = ({ isOpen, onClose, product, onSave }) => {
    const [formData, setFormData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (product) {
            setFormData({
                _id: product._id, nome: product.nome || '',
                preco: product.preco != null ? String(product.preco) : '',
                stockGeral: product.stockGeral != null ? String(product.stockGeral) : '',
                paraVenda: product.paraVenda !== undefined ? product.paraVenda : true,
                paraEmprestimo: product.paraEmprestimo || false,
                codigoBarras: product.codigoBarras || '', unidade: product.unidade || 'unidade'
            });
        } else { setFormData(null); }
    }, [product]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        setFormData(prev => {
            if (!prev) return null;
            const updatedData = { ...prev, [name]: newValue };
            if (name === 'paraVenda' && checked) { updatedData.paraEmprestimo = false; }
            else if (name === 'paraEmprestimo' && checked) { updatedData.paraVenda = false; updatedData.preco = '0'; }
            else if (name === 'paraVenda' && !checked) { updatedData.preco = '0'; }
            return updatedData;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData) return;
        const stockNum = Number(formData.stockGeral);
        const precoNum = Number(formData.preco);

        if (!formData.nome.trim() || isNaN(stockNum) || stockNum < 0) { toast.warn("Nome e Stock (>=0) são obrigatórios."); return; }
        if (formData.paraVenda && (formData.preco === '' || isNaN(precoNum) || precoNum < 0)) { toast.warn("Preço (>=0) é obrigatório para itens de venda."); return; }

        setIsSaving(true);
        try {
            const finalPreco = formData.paraVenda ? precoNum : 0;
            const dataToSave = {
                _id: formData._id,
                nome: formData.nome,
                preco: finalPreco,
                stockGeral: stockNum,
                paraVenda: formData.paraVenda,
                paraEmprestimo: formData.paraEmprestimo,
                codigoBarras: formData.codigoBarras,
                unidade: formData.unidade
            };
            
            await onSave(dataToSave); // Chama a onSave
            onClose(); // Fecha o modal após sucesso
        } catch (error) {
            console.error("Erro no submit do EditProductModal:", error);
            // Não fecha o modal em caso de erro
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen || !formData) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[80] p-4 modal-overlay">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-0 relative max-h-[90vh] flex flex-col">
                 <header className="flex justify-between items-center p-4 border-b sticky top-0 bg-white rounded-t-lg z-10">
                     <h2 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center gap-2"><FaEdit /> Editar Produto</h2>
                    <button onClick={onClose} disabled={isSaving} className="text-gray-400 hover:text-gray-600 no-print disabled:opacity-50"><FaTimes size={20} /></button>
                 </header>
                <form id={`edit-product-form-${formData._id}`} onSubmit={handleSubmit} className="space-y-3 sm:space-y-4 overflow-y-auto flex-grow p-4 sm:p-6">
                    <div> <label htmlFor={`edit-nome-${formData._id}`} className="block text-xs sm:text-sm font-medium text-gray-600 mb-1">Nome*</label> <input id={`edit-nome-${formData._id}`} name="nome" value={formData.nome} onChange={handleInputChange} required className="w-full p-2 border rounded text-sm"/> </div>
                    <div> <label htmlFor={`edit-codigoBarras-${formData._id}`} className="block text-xs sm:text-sm font-medium text-gray-600 mb-1">Cód. Barras/SKU</label> <input id={`edit-codigoBarras-${formData._id}`} name="codigoBarras" value={formData.codigoBarras} onChange={handleInputChange} className="w-full p-2 border rounded text-sm"/> </div>
                     <div className="grid grid-cols-2 gap-3 sm:gap-4">
                         <div> <label htmlFor={`edit-stockGeral-${formData._id}`} className="block text-xs sm:text-sm font-medium text-gray-600 mb-1">Stock Central*</label> <input id={`edit-stockGeral-${formData._id}`} name="stockGeral" type="number" value={formData.stockGeral} onChange={handleInputChange} min="0" required className="w-full p-2 border rounded text-sm"/> </div>
                        <div> <label htmlFor={`edit-unidade-${formData._id}`} className="block text-xs sm:text-sm font-medium text-gray-600 mb-1">Unidade*</label> <select id={`edit-unidade-${formData._id}`} name="unidade" value={formData.unidade} onChange={handleInputChange} required className="w-full p-2 border rounded bg-white text-sm"> <option value="unidade">Unidade</option> <option value="kg">Kg</option> <option value="litro">Litro</option> <option value="pacote">Pacote</option> </select> </div>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-2 items-center pt-1 sm:pt-2">
                         <div className="flex items-center"> <input type="checkbox" id={`edit-paraVenda-${formData._id}`} name="paraVenda" checked={formData.paraVenda} onChange={handleInputChange} className="checkbox checkbox-primary checkbox-xs sm:checkbox-sm" /> <label htmlFor={`edit-paraVenda-${formData._id}`} className="ml-1.5 text-xs sm:text-sm text-gray-700 cursor-pointer">Venda?</label> </div>
                        <div className="flex items-center"> <input type="checkbox" id={`edit-paraEmprestimo-${formData._id}`} name="paraEmprestimo" checked={formData.paraEmprestimo} onChange={handleInputChange} className="checkbox checkbox-secondary checkbox-xs sm:checkbox-sm" disabled={formData.paraVenda} /> <label htmlFor={`edit-paraEmprestimo-${formData._id}`} className={`ml-1.5 text-xs sm:text-sm cursor-pointer ${formData.paraVenda ? 'text-gray-400' : 'text-gray-700'}`}>Empréstimo?</label> </div>
                    </div>
                    {formData.paraVenda && (
                        <div className="pt-1 sm:pt-2"> <label htmlFor={`edit-preco-${formData._id}`} className="block text-xs sm:text-sm font-medium text-gray-600 mb-1">Preço (R$)*</label> <input id={`edit-preco-${formData._id}`} name="preco" type="number" value={formData.preco} onChange={handleInputChange} min="0" step="0.01" required={formData.paraVenda} className="w-full p-2 border rounded text-sm"/> </div>
                    )}
                </form>
                <footer className="flex justify-end gap-2 sm:gap-4 p-3 sm:p-4 border-t mt-auto flex-shrink-0 no-print sticky bottom-0 bg-white rounded-b-lg z-10">
                    <button type="button" onClick={onClose} disabled={isSaving} className="btn btn-ghost btn-sm sm:btn-md disabled:opacity-50">Cancelar</button>
                    <button type="submit" form={`edit-product-form-${formData._id}`} className="btn btn-primary btn-sm sm:btn-md" disabled={isSaving}>
                        {isSaving ? 'Salvando...' : <><FaSave className="mr-1 sm:mr-2"/> Salvar</>}
                    </button>
                </footer>
            </div>
        </div>
    );
};


// --- MODAL: Transferir Stock ---
// (Sem alterações)
const TransferStockModal = ({ produtos, pontosVenda, onClose, onSave }) => {
// ... (código mantido) ...
    const [produtoId, setProdutoId] = useState(''); const [pontoVendaId, setPontoVendaId] = useState(''); const [quantidade, setQuantidade] = useState(1); const produtoSelecionado = useMemo(() => produtos.find(p => p._id === produtoId), [produtos, produtoId]);
    const handleSubmit = (e) => { e.preventDefault(); if (!produtoSelecionado){ toast.warn('Selecione produto.'); return; } if (!pontoVendaId){ toast.warn('Selecione destino.'); return; } if (Number(quantidade) > (produtoSelecionado.stockGeral || 0)) { toast.warn('Qtd excede stock.'); return; } if (Number(quantidade) < 1) { toast.warn('Qtd >= 1.'); return; } onSave({ produtoId, pontoVendaId, quantidade: Number(quantidade) }); };
    return ( <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[70] p-4 modal-overlay"> <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 relative"> <button onClick={onClose} className="text-gray-400 hover:text-gray-600 absolute top-3 right-3 no-print"><FaTimes size={20} /></button> <h2 className="text-xl font-bold mb-4 text-gray-800">Transferir Stock</h2> <form onSubmit={handleSubmit} className="space-y-4"> <div> <label className="block text-sm font-medium text-gray-700 mb-1">Produto (Stock Central)</label> <select value={produtoId} onChange={e => setProdutoId(e.target.value)} required className="w-full p-2 border rounded bg-white text-sm"> <option value="">Selecione...</option> {produtos.filter(p => p.stockGeral > 0).map(p => ( <option key={p._id} value={p._id}>{p.nome} ({p.stockGeral} un.)</option> ))} </select> </div> <div> <label className="block text-sm font-medium text-gray-700 mb-1">Ponto de Venda Destino</label> <select value={pontoVendaId} onChange={e => setPontoVendaId(e.target.value)} required className="w-full p-2 border rounded bg-white text-sm"> <option value="">Selecione...</option> {pontosVenda.map(pv => <option key={pv._id} value={pv._id}>{pv.nome}</option>)} </select> </div> <div> <label className="block text-sm font-medium text-gray-700 mb-1">Quantidade</label> <input type="number" value={quantidade} onChange={e => setQuantidade(e.target.value)} min="1" max={produtoSelecionado?.stockGeral || 1} required className="w-full p-2 border rounded text-sm" disabled={!produtoSelecionado} /> {produtoSelecionado && ( <p className="text-xs text-gray-500 mt-1">Máx: {produtoSelecionado.stockGeral}</p> )} </div> <div className="flex justify-end gap-2 sm:gap-4 pt-4 border-t"> <button type="button" onClick={onClose} className="btn btn-ghost no-print btn-sm sm:btn-md">Cancelar</button> <button type="submit" className="btn btn-primary no-print btn-sm sm:btn-md" disabled={!produtoSelecionado || !pontoVendaId || quantidade < 1}>Transferir</button> </div> </form> </div> </div> );
};


// --- Componente de Login ---
// (Sem alterações)
const LoginPage = ({ onLoginSuccess }) => {
// ... (código mantido) ...
    const [email, setEmail] = useState(''); const [password, setPassword] = useState(''); const [loading, setLoading] = useState(false); const handleSubmit = async (e) => { e.preventDefault(); setLoading(true); try { const apiUrl = process.env.REACT_APP_API_URL || 'https://sistema-hotel-react.onrender.com'; const { data } = await axios.post(`${apiUrl}/api/auth/login`, { email, password }); if (data.role === 'almoxarifado' || data.role === 'admin') { onLoginSuccess(data); toast.success('Login sucesso!'); } else { toast.error('Acesso negado.'); } } catch (error) { toast.error(error.response?.data?.message || 'Credenciais inválidas.'); } finally { setLoading(false); } };
    return ( <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4"> <div className="w-full max-w-md p-6 sm:p-8 space-y-6 bg-white rounded-lg shadow-md"> <div className="text-center"> <FaWarehouse className="mx-auto text-4xl text-indigo-600"/> <h1 className="text-xl sm:text-2xl font-bold mt-2 text-gray-800">Portal Almoxarifado</h1> </div> <form onSubmit={handleSubmit} className="space-y-4"> <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="E-mail" required className="w-full p-3 border rounded-lg text-sm sm:text-base"/> <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Senha" required className="w-full p-3 border rounded-lg text-sm sm:text-base"/> <button type="submit" disabled={loading} className="w-full btn btn-primary btn-sm sm:btn-md"> {loading ? 'Entrando...' : 'Entrar'} </button> </form> </div> </div> );
};

// --- Componente do Dashboard ---
const DashboardPage = ({ userInfo, onLogout }) => {
    usePrintStyles();
    const [produtosVenda, setProdutosVenda] = useState([]);
    const [itensEstoque, setItensEstoque] = useState([]);
    const [pontosVenda, setPontosVenda] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFetchingData, setIsFetchingData] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showScanner, setShowScanner] = useState(false);
    const [filtro, setFiltro] = useState('');
    const [novoItem, setNovoItem] = useState({ nome: '', preco: '', stockGeral: '', paraVenda: true, codigoBarras: '', paraEmprestimo: false, unidade: 'unidade' });
    const [showQrModal, setShowQrModal] = useState(false);
    const [produtoParaQr, setProdutoParaQr] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);

    const config = useMemo(() => ({ headers: { Authorization: `Bearer ${userInfo.token}` } }), [userInfo.token]);
    const apiUrl = process.env.REACT_APP_API_URL || 'https://sistema-hotel-react.onrender.com';

    const fetchData = useCallback(async (isInitialLoad = false) => {
        if (!isInitialLoad && !isFetchingData) setIsFetchingData(true);
        try {
            const { data } = await axios.get(`${apiUrl}/api/almoxarifado/dashboard`, config);
            setProdutosVenda(data.produtosParaVenda || []);
            setItensEstoque(data.itensDeEstoque || []);
            setPontosVenda(data.pontosVenda || []);
        } catch (error) {
             if (error.response?.status === 401 || error.response?.status === 403) { if (isInitialLoad || loading) { toast.error("Sessão inválida."); onLogout(); } console.error("Auth Error:", error); }
             else { if (isInitialLoad) toast.error("Falha ao carregar dados."); console.error("Fetch Error:", error); }
        } finally { if (isInitialLoad) setLoading(false); setIsFetchingData(false); }
    }, [apiUrl, config, onLogout, isFetchingData, loading]);

    useEffect(() => {
        fetchData(true);
        const intervalId = setInterval(() => fetchData(false), 30000);
        return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // fetchData agora tem dependências estáveis

    // **Scan-to-Edit (Correção Lógica)**
    useEffect(() => {
        if (editingProduct) {
            setIsEditModalOpen(true);
        }
    }, [editingProduct]);

    const handleAddItem = async (e) => {
        e.preventDefault(); const stockNum = Number(novoItem.stockGeral); const precoNum = Number(novoItem.preco); if (!novoItem.nome || isNaN(stockNum) || stockNum < 0 || (novoItem.paraVenda && (novoItem.preco==='' || isNaN(precoNum) || precoNum < 0))) { toast.warn("Verifique campos."); return; }
        try { const dados = {...novoItem, preco: novoItem.paraVenda ? precoNum : 0, stockGeral: stockNum }; if (!dados.paraVenda) delete dados.preco;
            const { data: p } = await axios.post(`${apiUrl}/api/produtos`, dados, config);
            toast.success('Adicionado!'); fetchData(false); setNovoItem({ nome: '', preco: '', stockGeral: '', paraVenda: true, codigoBarras: '', paraEmprestimo: false, unidade: 'unidade' }); if (p && (p._id || p.codigoBarras) ) { setProdutoParaQr(p); setShowQrModal(true); } else { toast.info("QR indisponível."); }
        } catch (error) { toast.error(error.response?.data?.message || 'Erro.'); }
    };

    const handleTransferStock = async (transferData) => {
        try { await axios.post(`${apiUrl}/api/almoxarifado/transferir`, transferData, config); toast.success('Transferido!'); fetchData(false); setShowTransferModal(false); } catch (error) { toast.error(error.response?.data?.message || 'Erro.'); }
    };

    // **Scan-to-Edit (Correção Lógica)**
    const handleBarcodeScanned = async (code) => {
        setShowScanner(false);
        setIsFetchingData(true);
        try {
             const { data: produtoEncontrado } = await axios.get(`${apiUrl}/api/produtos/barcode/${code}`, config);
            if (produtoEncontrado && produtoEncontrado._id) {
                 toast.success(`Produto "${produtoEncontrado.nome}" encontrado.`);
                 setEditingProduct(produtoEncontrado); // Define o produto
                 setFiltro('');
                 // O useEffect acima abre o modal
            }
        } catch (error) {
            if (error.response?.status === 404) { toast.warn('Nenhum produto encontrado.'); }
            else { toast.error('Erro ao buscar produto.'); console.error("Scan Error:", error); }
        } finally { setIsFetchingData(false); }
    };


    // **Salvar Edição (Correção Lógica)**
    const handleSaveEdit = (editedProductData) => {
        return new Promise(async (resolve, reject) => {
             if (!editedProductData?._id) {
                 const err = new Error("ID Inválido"); toast.error("Erro: ID inválido."); return reject(err);
             }
             try {
                 await axios.put(`${apiUrl}/api/produtos/${editedProductData._id}`, editedProductData, config);
                 toast.success(`Produto "${editedProductData.nome}" atualizado!`);
                 setEditingProduct(null); // Limpa
                 fetchData(false); // Atualiza
                 resolve(); // Sucesso
             } catch (error) {
                 toast.error(error.response?.data?.message || 'Erro ao salvar.');
                 console.error("Erro ao salvar:", error);
                 reject(error); // Erro
             }
         });
    };

    // **Função para fechar o modal e limpar estado**
    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setEditingProduct(null); // Limpa o produto
    };


    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target; const newValue = type === 'checkbox' ? checked : value; if (name === 'paraVenda' && checked) { setNovoItem(prev => ({ ...prev, paraVenda: true, paraEmprestimo: false })); } else if (name === 'paraEmprestimo' && checked) { setNovoItem(prev => ({ ...prev, paraVenda: false, paraEmprestimo: true, preco: '' })); } else { setNovoItem(prev => ({ ...prev, [name]: newValue })); }
    };
    const handleViewQrClick = (produto) => { setProdutoParaQr(produto); setShowQrModal(true); };

    const produtosVendaFiltrados = produtosVenda.filter(p => p.nome.toLowerCase().includes(filtro.toLowerCase()));
    const itensEstoqueFiltrados = itensEstoque.filter(p => p.nome.toLowerCase().includes(filtro.toLowerCase()));
    const colunasVendaCount = 3 + pontosVenda.length;

    // Classes de estilo responsivo
    const thClasses = "p-2 sm:p-3 text-xs sm:text-sm font-semibold uppercase tracking-wider text-gray-600 bg-gray-100";
    const tdClasses = "p-2 sm:p-3 text-xs sm:text-sm text-gray-700";
    const stickyLeftClasses = "sticky left-0 z-10";
    const stickyRightClasses = "sticky right-0 z-10 whitespace-nowrap";
    const stickyHeaderLeftClasses = "sticky left-0 z-20 bg-gray-100";
    const stickyHeaderRightClasses = "sticky right-0 z-20 bg-gray-100 whitespace-nowrap";


    return (
        <div className="bg-gray-100 min-h-screen">
             {/* Modais */}
             {showTransferModal && <TransferStockModal produtos={produtosVenda} pontosVenda={pontosVenda} onClose={() => setShowTransferModal(false)} onSave={handleTransferStock} />}
             {showScanner && <BarcodeScannerModal onClose={() => setShowScanner(false)} onScan={handleBarcodeScanned} isFetchingData={isFetchingData} />}
             <QrCodeModal isOpen={showQrModal} onClose={() => setShowQrModal(false)} product={produtoParaQr} />
             <EditProductModal isOpen={isEditModalOpen} onClose={handleCloseEditModal} product={editingProduct} onSave={handleSaveEdit} />

            {/* Header Fixo e Responsivo */}
            <header className="bg-white shadow-md p-3 sm:p-4 flex justify-between items-center sticky top-0 z-40">
                <h1 className="text-lg sm:text-xl font-bold flex items-center gap-2 text-gray-800 whitespace-nowrap"><FaWarehouse size={20} className="sm:size-6"/> Almoxarifado</h1>
                <div className="flex flex-wrap gap-1 sm:gap-2">
                    <button onClick={() => setShowTransferModal(true)} className="btn btn-secondary btn-xs sm:btn-sm flex items-center"><FaExchangeAlt className="mr-1 hidden sm:inline"/> Transf.</button>
                    <button onClick={onLogout} className="btn btn-ghost btn-xs sm:btn-sm flex items-center"><FaSignOutAlt className="mr-1 hidden sm:inline"/> Sair</button>
                </div>
            </header>

            {/* Conteúdo Principal Responsivo */}
            <main className="p-3 sm:p-6 grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                 {/* Coluna Adicionar Item */}
                <div className="lg:col-span-1 order-last lg:order-first">
                    <div className="card bg-white shadow-lg rounded-lg p-4 sm:p-6 lg:sticky lg:top-20">
                         <h2 className="text-base sm:text-xl font-semibold mb-3 sm:mb-4 text-gray-700">Adicionar Item</h2>
                         <form onSubmit={handleAddItem} className="space-y-3 sm:space-y-4">
                             <div> <label htmlFor="nome" className="block text-xs sm:text-sm font-medium text-gray-600 mb-1">Nome*</label> <input id="nome" name="nome" value={novoItem.nome} onChange={handleInputChange} required className="w-full p-2 border rounded text-sm"/> </div>
                            <div> <label htmlFor="codigoBarras" className="block text-xs sm:text-sm font-medium text-gray-600 mb-1">Cód. Barras/SKU</label> <input id="codigoBarras" name="codigoBarras" value={novoItem.codigoBarras} onChange={handleInputChange} className="w-full p-2 border rounded text-sm"/> </div>
                            <div className="grid grid-cols-2 gap-3 sm:gap-4">
                                <div> <label htmlFor="stockGeral" className="block text-xs sm:text-sm font-medium text-gray-600 mb-1">Stock*</label> <input id="stockGeral" name="stockGeral" type="number" value={novoItem.stockGeral} onChange={handleInputChange} min="0" required className="w-full p-2 border rounded text-sm"/> </div>
                                <div> <label htmlFor="unidade" className="block text-xs sm:text-sm font-medium text-gray-600 mb-1">Unidade*</label> <select id="unidade" name="unidade" value={novoItem.unidade} onChange={handleInputChange} required className="w-full p-2 border rounded bg-white text-sm"> <option value="unidade">Unidade</option> <option value="kg">Kg</option> <option value="litro">Litro</option> <option value="pacote">Pacote</option> </select> </div>
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-2 items-center pt-1 sm:pt-2">
                                <div className="flex items-center"> <input type="checkbox" id="paraVenda" name="paraVenda" checked={novoItem.paraVenda} onChange={handleInputChange} className="checkbox checkbox-primary checkbox-xs sm:checkbox-sm" /> <label htmlFor="paraVenda" className="ml-1.5 text-xs sm:text-sm text-gray-700 cursor-pointer">Venda?</label> </div>
                                <div className="flex items-center"> <input type="checkbox" id="paraEmprestimo" name="paraEmprestimo" checked={novoItem.paraEmprestimo} onChange={handleInputChange} className="checkbox checkbox-secondary checkbox-xs sm:checkbox-sm" disabled={novoItem.paraVenda} /> <label htmlFor="paraEmprestimo" className={`ml-1.5 text-xs sm:text-sm cursor-pointer ${novoItem.paraVenda ? 'text-gray-400' : 'text-gray-700'}`}>Empréstimo?</label> </div>
                            </div>
                            {novoItem.paraVenda && (
                                <div className="pt-1 sm:pt-2"> <label htmlFor="preco" className="block text-xs sm:text-sm font-medium text-gray-600 mb-1">Preço (R$)*</label> <input id="preco" name="preco" type="number" value={novoItem.preco} onChange={handleInputChange} min="0" step="0.01" required={novoItem.paraVenda} className="w-full p-2 border rounded text-sm"/> </div>
                            )}
                            <button type="submit" className="btn btn-primary w-full mt-3 sm:mt-4 btn-sm sm:btn-md"><FaPlus className="mr-1 sm:mr-2"/> Adicionar</button>
                        </form>
                    </div>
                </div>

                {/* Coluna Listas */}
                <div className="lg:col-span-2 space-y-6 sm:space-y-8">
                     {/* Barra de Pesquisa e Scanner */}
                    <div className="flex flex-col sm:flex-row gap-2">
                        <div className="relative flex-grow">
                            <FaSearch className="absolute top-1/2 left-3 sm:left-4 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            <input type="text" placeholder="Pesquisar..." value={filtro} onChange={e => setFiltro(e.target.value)} className="w-full pl-10 sm:pl-12 pr-4 py-2 sm:py-3 border rounded-lg bg-white text-sm sm:text-base"/>
                        </div>
                        <button onClick={() => setShowScanner(true)} className="btn btn-outline btn-secondary p-2 sm:p-3 flex-shrink-0 text-xs sm:text-sm flex items-center justify-center sm:justify-start" title="Ler/Digitar Código">
                            <FaBarcode size={18}/>
                            <span className="ml-2 hidden sm:inline">Scan/Digitar</span>
                             <span className="ml-1 sm:hidden">Scan</span>
                        </button>
                    </div>

                    {/* Tabela Produtos de Venda */}
                    <div className="card bg-white shadow-lg rounded-lg overflow-hidden">
                        <div className="p-3 sm:p-4 border-b flex justify-between items-center bg-gray-50 rounded-t-lg">
                             <h2 className="text-sm sm:text-lg font-semibold text-gray-700">Produtos de Venda</h2>
                             {(loading || isFetchingData) && <span className="loading loading-spinner loading-xs text-gray-400"></span>}
                         </div>
                         {loading ? (<div className="p-4 text-center text-gray-500">A carregar produtos...</div>) : (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[700px] text-xs sm:text-sm">
                                    <thead className="bg-gray-100">
                                        <tr className="text-left text-gray-600 uppercase text-xs">
                                            <th className={`${thClasses} ${stickyHeaderLeftClasses} left-0`}>Produto</th>
                                            <th className={`${thClasses} text-center`}>Stock</th>
                                            {pontosVenda.map(pv => <th key={pv._id} className={`${thClasses} text-center whitespace-nowrap`}>{pv.nome}</th>)}
                                            <th className={`${thClasses} text-center ${stickyHeaderRightClasses} right-0`}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {produtosVendaFiltrados.length > 0 ? produtosVendaFiltrados.map(p => (
                                            <tr key={p._id} className="hover:bg-gray-50 align-middle group">
                                                <td className={`${tdClasses} font-medium text-gray-900 ${stickyLeftClasses} bg-white group-hover:bg-gray-50`}>{p.nome}</td>
                                                <td className={`${tdClasses} text-center font-semibold text-indigo-600`}>{p.stockGeral}</td>
                                                {pontosVenda.map(pv => { const stockPonto = p.stockPontosVenda?.find(s => s.pontoVenda === pv._id || s.pontoVenda?._id === pv._id); return <td key={pv._id} className={`${tdClasses} text-center`}>{stockPonto?.quantidade || 0}</td>; })}
                                                <td className={`${tdClasses} text-center ${stickyRightClasses} bg-white group-hover:bg-gray-50`}>
                                                     <button onClick={() => handleViewQrClick(p)} className="btn btn-ghost btn-xs text-gray-500 hover:text-indigo-600 p-1" title="Ver QR"> <FaQrcode size={14}/> </button>
                                                     <button onClick={() => setEditingProduct(p)} className="btn btn-ghost btn-xs text-gray-500 hover:text-blue-600 p-1" title="Editar"> <FaEdit size={14}/> </button>
                                                </td>
                                            </tr>
                                        )) : ( <tr><td colSpan={colunasVendaCount} className={`${tdClasses} text-center text-gray-500 py-4`}>Nenhum produto encontrado {filtro && 'com este filtro'}.</td></tr> )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Tabela Itens de Uso Interno */}
                    <div className="card bg-white shadow-lg rounded-lg overflow-hidden">
                         <div className="p-3 sm:p-4 border-b flex justify-between items-center bg-gray-50 rounded-t-lg">
                            <h2 className="text-sm sm:text-lg font-semibold text-gray-700">Itens Uso Interno/Empréstimo</h2>
                            {(loading || isFetchingData) && <span className="loading loading-spinner loading-xs text-gray-400"></span>}
                        </div>
                         {loading ? (<div className="p-4 text-center text-gray-500">A carregar itens...</div>) : (
                            <div className="overflow-x-auto">
                               <table className="w-full min-w-[400px] text-xs sm:text-sm">
                                    <thead className="bg-gray-100">
                                        <tr className="text-left text-gray-600 uppercase text-xs">
                                            <th className={`${thClasses} ${stickyHeaderLeftClasses} left-0`}>Item</th>
                                            <th className={`${thClasses} text-center`}>Stock</th>
                                            <th className={`${thClasses} text-center ${stickyHeaderRightClasses} right-0`}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {itensEstoqueFiltrados.length > 0 ? itensEstoqueFiltrados.map(p => (
                                            <tr key={p._id} className="hover:bg-gray-50 align-middle group">
                                                <td className={`${tdClasses} font-medium text-gray-900 ${stickyLeftClasses} bg-white group-hover:bg-gray-50`}>{p.nome}</td>
                                                <td className={`${tdClasses} text-center font-semibold text-indigo-600`}>{p.stockGeral}</td>
                                                <td className={`${tdClasses} text-center ${stickyRightClasses} bg-white group-hover:bg-gray-50`}>
                                                     <button onClick={() => handleViewQrClick(p)} className="btn btn-ghost btn-xs text-gray-500 hover:text-indigo-600 p-1" title="Ver QR"> <FaQrcode size={14}/> </button>
                                                     <button onClick={() => setEditingProduct(p)} className="btn btn-ghost btn-xs text-gray-500 hover:text-blue-600 p-1" title="Editar"> <FaEdit size={14}/> </button>
                                                </td>
                                            </tr>
                                        )) : ( <tr><td colSpan="3" className={`${tdClasses} text-center text-gray-500 py-4`}>Nenhum item encontrado {filtro && 'com este filtro'}.</td></tr> )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

// --- Componente Principal da App ---
function App() {
    usePrintStyles();
    const [userInfo, setUserInfo] = useState(() => { /* ... (lógica mantida) ... */
        try { const storedInfo = localStorage.getItem('almoxarifado_userInfo'); if (!storedInfo) return null; const parsedInfo = JSON.parse(storedInfo); if (['almoxarifado', 'admin'].includes(parsedInfo.role)) { return parsedInfo; } else { localStorage.removeItem('almoxarifado_userInfo'); return null; } } catch { return null; }
    });
    const handleLogin = (newUserInfo) => { /* ... (lógica mantida) ... */
        if (['almoxarifado', 'admin'].includes(newUserInfo.role)) { localStorage.setItem('almoxarifado_userInfo', JSON.stringify(newUserInfo)); setUserInfo(newUserInfo); } else { toast.error("Role inválida."); }
    };
    const handleLogout = () => { localStorage.removeItem('almoxarifado_userInfo'); setUserInfo(null); };

    return (
        <Router>
            {/* Toast Container com z-index alto */}
            <ToastContainer position="top-center" autoClose={3000} hideProgressBar newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" style={{ zIndex: 99999 }} /> {/* Z-index muito alto */}
            <Routes>
                <Route path="/" element={!userInfo ? <LoginPage onLoginSuccess={handleLogin} /> : <Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={userInfo ? <DashboardPage userInfo={userInfo} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
                <Route path="*" element={<Navigate to={userInfo ? "/dashboard" : "/"} replace />} />
            </Routes>
        </Router>
    );
}

export default App;

